import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  GridPaginationModel,
  GridCallbackDetails,
} from '@mui/x-data-grid-pro';
import {
  isInspectionDetailsPath,
  isIssuesOrInspectionsListPath,
  isIssueDetailsPath,
} from 'setup/navigation/routeMatchers';

interface UseDataGridUrlPaginationOptions {
  defaultPageSize: number;
}

interface UseDataGridUrlPaginationResult {
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (
    model: GridPaginationModel,
    details: GridCallbackDetails
  ) => void;
  goToFirstPage: () => void;
}

interface IssueNavigationState {
  fromIssueDetails?: boolean;
  fromInspectionDetails?: boolean;
}

/**
 * Hook to sync DataGrid pagination with URL.
 * Keeps URL as source of truth and ignores one technical page=0 callback
 * right after returning from issue details.
 */
function getPageFromSearchParams(searchParams: URLSearchParams): number {
  const pageParam = searchParams.get('page');
  if (pageParam) {
    const parsed = parseInt(pageParam, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      return parsed;
    }
  }
  return 0;
}

function withUpdatedPageSearchParam(
  prev: URLSearchParams,
  page: number
): URLSearchParams {
  const next = new URLSearchParams(prev);

  next.set('page', Math.max(0, page).toString());

  return next;
}

function hasReturnedFromIssueDetails(state: unknown): boolean {
  return (
    ((state as IssueNavigationState | null) ?? {}).fromIssueDetails ===
    true
  );
}

function hasReturnedFromInspectionDetails(state: unknown): boolean {
  return (
    ((state as IssueNavigationState | null) ?? {})
      .fromInspectionDetails === true
  );
}

export function useDataGridUrlPagination({
  defaultPageSize,
}: UseDataGridUrlPaginationOptions): UseDataGridUrlPaginationResult {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();

  const previousPathnameRef = useRef(location.pathname);
  const ignoreNextForcedPageZeroRef = useRef(false);

  const currentUrlPage = useMemo(
    () => getPageFromSearchParams(new URLSearchParams(location.search)),
    [location.search]
  );

  const paginationModel = useMemo((): GridPaginationModel => {
    return { page: currentUrlPage, pageSize: defaultPageSize };
  }, [currentUrlPage, defaultPageSize]);

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const currentPathname = location.pathname;
    const cameFromIssueDetailsByState = hasReturnedFromIssueDetails(
      location.state
    );
    const cameFromInspectionDetailsByState =
      hasReturnedFromInspectionDetails(location.state);

    const enteredList = isIssuesOrInspectionsListPath(currentPathname);
    const cameFromSameList = previousPathname === currentPathname;
    const cameFromIssueDetails = isIssueDetailsPath(previousPathname);
    const cameFromInspectionDetails = isInspectionDetailsPath(
      previousPathname
    );
    const cameFromDetails =
      cameFromIssueDetails ||
      cameFromInspectionDetails ||
      cameFromIssueDetailsByState ||
      cameFromInspectionDetailsByState;

    if (enteredList && cameFromDetails) {
      ignoreNextForcedPageZeroRef.current = true;
    }

    if (!enteredList) {
      ignoreNextForcedPageZeroRef.current = false;
    }

    const shouldResetToFirstPage =
      enteredList && !cameFromSameList && !cameFromDetails;

    previousPathnameRef.current = currentPathname;

    if (!shouldResetToFirstPage || currentUrlPage <= 0) {
      return;
    }

    setSearchParams(
      (prev) => withUpdatedPageSearchParam(prev, 0),
      { replace: true }
    );
  }, [
    location.pathname,
    location.state,
    currentUrlPage,
    setSearchParams,
  ]);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel, _details: GridCallbackDetails) => {
      const shouldIgnoreForcedPageZero =
        ignoreNextForcedPageZeroRef.current &&
        model.page === 0 &&
        currentUrlPage > 0;

      if (shouldIgnoreForcedPageZero) {
        ignoreNextForcedPageZeroRef.current = false;
        return;
      }

      if (model.page === currentUrlPage) {
        return;
      }

      ignoreNextForcedPageZeroRef.current = false;

      setSearchParams(
        (prev) => withUpdatedPageSearchParam(prev, model.page),
        { replace: true }
      );
    },
    [currentUrlPage, setSearchParams]
  );

  const goToFirstPage = useCallback(() => {
    if (currentUrlPage <= 0) {
      return;
    }

    ignoreNextForcedPageZeroRef.current = false;

    setSearchParams(
      (prev) => withUpdatedPageSearchParam(prev, 0),
      { replace: true }
    );
  }, [currentUrlPage, setSearchParams]);

  return {
    paginationModel,
    onPaginationModelChange,
    goToFirstPage,
  };
}
